import {isNil, isNotNil} from 'ramda';

import {omneticApi} from '@dms/api/base';

import {assignSafely, getContentDispositionFileName, getSafely} from 'shared/web';

import {
  updateAuctionDetailCacheByExternalId,
  updateAuctionVehicleSummaryInCaches,
} from '../sourcingAuction/api';
import {ROWS_PER_PAGE} from './constants/rowsPerPage';
import {SOURCING_KEEP_UNUSED_DATA_SECONDS} from './constants/sourcingKeepUnusedDataSeconds';
import {
  AdServerRedirectApiArg,
  AdServerRedirectResponse,
  AllowedCountryAndCurrencyApiArg,
  AllowedCountryAndCurrencyApiResponse,
  AvailabilityCheckStatus,
  SourcingAvailabilityByAdIdApiArg,
  SourcingAvailabilityByAdIdApiResponse,
  SourcingAvailabilityCheckApiArg,
  SourcingAvailabilityCheckApiResponse,
  BulkCreateUserCarvagoBuyApiArg,
  BulkCreateUserCarvagoBuyApiResponse,
  BulkCreateUserComparisonApiArg,
  BulkCreateUserComparisonApiResponse,
  BulkCreateUserFavouriteApiArg,
  BulkCreateUserFavouriteApiResponse,
  BulkCreateUserHiddenApiArg,
  BulkCreateUserHiddenApiResponse,
  BulkCreateUserToBuyApiArg,
  BulkCreateUserToBuyApiResponse,
  BulkDeleteUserCarvagoBuyApiArg,
  BulkDeleteUserCarvagoBuyApiResponse,
  BulkDeleteUserComparisonApiArg,
  BulkDeleteUserComparisonApiResponse,
  BulkDeleteUserFavouriteApiArg,
  BulkDeleteUserFavouriteApiResponse,
  BulkDeleteUserHiddenApiArg,
  BulkDeleteUserHiddenApiResponse,
  BulkDeleteUserToBuyApiArg,
  BulkDeleteUserToBuyApiResponse,
  CreateRelativeFilterApiArg,
  CreateRelativeFilterApiResponse,
  CreatePresetApiResponse,
  CreateUserCarvagoBuyApiArg,
  CreateUserCarvagoBuyApiResponse,
  CreateUserSeenApiArg,
  CreateUserSeenApiResponse,
  CreateUserVehicleCostApiArg,
  CreateUserVehicleCostsApiArg,
  CreateUserVehicleSummaryApiArg,
  CreateUserVehicleSummaryRequestBody,
  DeleteUserCarvagoBuyApiArg,
  DeleteUserCarvagoBuyApiResponse,
  DeleteUserVehicleCostApiArg,
  DeleteRelativeFilterApiArg,
  DeleteRelativeFilterApiResponse,
  DeletePresetApiResponse,
  DetailDefaultUserPresetCostApiResponse,
  DetailUserCostVehicleNoteApiArg,
  DetailUserCostVehicleNoteApiResponse,
  DrillDownAdServerRedirectApiArg,
  DrillDownAdServerRedirectApiResponse,
  DrillDownApiArg,
  DrillDownResponse,
  RawDrillDownResponse,
  ExportAllUserToBuyToCsvApiArg,
  ExportVehiclesToCsvApiArg,
  GetCarFeaturesApiArg,
  GetCarFeaturesApiResponse,
  GetPresetApiArg,
  GetPresetApiResponse,
  GetPresetsListApiArg,
  GetPresetsListApiResponse,
  MarkPresetAllSeenApiResponse,
  GetRelativeFiltersApiArg,
  GetRelativeFiltersApiResponse,
  GetPriceMapApiResponse,
  GetPriceMapApiArg,
  GetSourcingVehicleCountApiArg,
  GetSourcingVehicleCountApiResponse,
  HighlightResponseBody,
  ShortcomingResponseBody,
  ListUserResponseBody,
  SourcingUserListQuery,
  PushVehiclesToSaleApiArg,
  RemoveHighlightBulkApiArg,
  RemoveHighlightBulkApiResponse,
  RemoveShortcomingBulkApiArg,
  RemoveShortcomingBulkApiResponse,
  ReorderRelativeFiltersApiArg,
  ReorderRelativeFiltersApiResponse,
  ReorderPresetsApiArg,
  ReorderPresetsApiResponse,
  SalesClaim,
  SetUserVehicleCostNoteApiArg,
  SortUserComparisonRequestApiArg,
  SortUserComparisonRequestBody,
  SourcingCreateCommentApiArg,
  SourcingCreateCommentApiResponse,
  SourcingCreatePresetApiArg,
  SourcingDeleteCommentApiArg,
  SourcingDeletePresetApiArg,
  SourcingGetCommentsApiArg,
  SourcingGetCommentsApiResponse,
  SourcingGetPriceReportApiArg,
  SourcingGetPriceReportApiResponse,
  SourcingGetTopSellingApiArg,
  SourcingGetTopSellingApiResponse,
  SourcingMarkPresetAllSeenApiArg,
  SourcingServer,
  SourcingUpdateCommentApiArg,
  SourcingUpdateCommentApiResponse,
  SourcingUpdatePresetApiArg,
  SourcingVehicleDetailApiArg,
  SourcingVehicleDetailApiResponse,
  CreateSourcingVehicleForBusinessCaseApiArg,
  CreateSourcingVehicleForBusinessCaseApiResponse,
  SourcingVehicleListApiArg,
  SourcingVehicleListApiResponse,
  SourcingVehicleLocationsApiArg,
  SourcingVehicleLocationsApiResponse,
  UpdateRelativeFilterApiArg,
  UpdateRelativeFilterApiResponse,
  UpdatePresetApiResponse,
  UpdateUserVehicleCostApiArg,
  UserCostVehicleListApiArg,
  UserCostVehicleListApiResponse,
  CreateSourcingVehicleOfferApiResponse,
  CreateSourcingVehicleOfferApiArg,
  GetSourcingVehicleOfferApiResponse,
  GetSourcingVehicleOfferApiArg,
  UserListCsvExport,
} from './types';
import {filterOutDuplicates} from './utils/filterOutDuplicates';
import {fixAlfaBugsInDrillDownResponse} from './utils/fixAlfaBugsInDrillDownResponse';
import {getInfiniteScrollBoundaryOffset} from './utils/getInfiniteScrollBoundaryOffset';
import {hasMoreUserListPages} from './utils/hasMoreUserListPages';
import {isSourcingVehicleListApiResponse} from './utils/isSourcingVehicleListApiResponse';
import {shouldPersistAvailabilityCheckResult} from './utils/shouldPersistAvailabilityCheckResult';
import {
  isSourcingListEndpointName,
  List,
  OmneticMutationLifecycleApi,
  setUserVehicleSummary,
} from './utils/sourcingApiHelpers';
import {updateListEndReached} from './utils/updateListEndReached';
import {updateScrollAttemptedOffset} from './utils/updateScrollAttemptedOffset';

export {isTerminalAvailabilityStatus} from './utils/isTerminalAvailabilityStatus';

const getOffset = (page: number = 1) => (page - 1) * ROWS_PER_PAGE;

const USER_LIST_TAGS = {
  'user-comparison': 'SourcingComparisonList',
  'user-favourite': 'SourcingFavouriteList',
  'user-hidden': 'SourcingHiddenList',
  'user-to-buy': 'SourcingToBuyList',
  'user-carvago-buy': 'SourcingCarvagoBuyList',
} as const;

/**
 * Reads the CSV together with its `Content-Disposition` name, the one place both are at hand.
 * Failures carry the shared API error JSON (DMS-SU-EX-0001 over the 1000-vehicle limit, …),
 * which `handleApiError` reads — reading them as text would turn them into a generic toast.
 */
const readUserListCsvExportResponse = async (response: Response): Promise<unknown> => {
  if (!response.ok) {
    const errorBody: unknown = await response.json().catch(() => null);

    return errorBody;
  }

  const csvExport: UserListCsvExport = {
    content: await response.text(),
    fileName: getContentDispositionFileName(response.headers.get('content-disposition')),
  };

  return csvExport;
};

const sourcingRtkApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    /*
      Exports a whole user list (any list, both sourcing types — the name predates that). A GET
      read triggered by a button, so callers use the lazy hook; the selection export below is
      the POST mutation.
    */
    exportAllUserToBuyToCsv: build.query<UserListCsvExport, ExportAllUserToBuyToCsvApiArg>({
      // The file is downloaded at once; holding the whole CSV in the store helps nothing.
      keepUnusedDataFor: 0,
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-to-buy/export/csv`,
        responseHandler: readUserListCsvExportResponse,
        cache: 'no-cache',
        params: {
          sourcingType: queryArg.sourcingType,
          list: queryArg.list,
          buyerCountry: queryArg.buyerCountry,
          currency: queryArg.currency,
          pricingStrategy: queryArg.pricingStrategy,
          margin: queryArg.margin,
          orderBy: queryArg.orderBy,
        },
      }),
    }),
    sourcingCreatePreset: build.mutation<CreatePresetApiResponse, SourcingCreatePresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets`,
        method: 'POST',
        body: queryArg.createPresetRequestBody,
      }),
      invalidatesTags: () => [{type: 'SourcingFilterPresetList'}],
    }),
    sourcingGetPresetsList: build.query<GetPresetsListApiResponse, GetPresetsListApiArg>({
      query: (queryArg) => ({
        url: '/dms/v1/sourcing/filter-presets',
        params: {country: queryArg.country, currency: queryArg.currency},
      }),
      providesTags: (result) =>
        result
          ? [
              {type: 'SourcingFilterPresetList'},
              ...result.map((item) => ({type: 'SourcingFilterPreset' as const, id: item.id})),
            ]
          : [{type: 'SourcingFilterPresetList'}],
    }),
    sourcingUpdatePreset: build.mutation<UpdatePresetApiResponse, SourcingUpdatePresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets/${queryArg.id}`,
        method: 'PATCH',
        body: queryArg.updatePresetRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [{type: 'SourcingFilterPreset', id: queryArg.id}],
    }),
    sourcingDeletePreset: build.mutation<DeletePresetApiResponse, SourcingDeletePresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets/${queryArg.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, queryArg) => [{type: 'SourcingFilterPreset', id: queryArg.id}],
    }),
    sourcingMarkPresetAllSeen: build.mutation<
      MarkPresetAllSeenApiResponse,
      SourcingMarkPresetAllSeenApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets/${queryArg.id}/mark-all-seen`,
        method: 'POST',
      }),
      onQueryStarted(queryArg, mutationLifecycleApi) {
        optimisticResetPresetNewCount(queryArg.id, mutationLifecycleApi);
        optimisticClearIsNewInPresetListCaches(queryArg.id, mutationLifecycleApi);
      },
      invalidatesTags: () => [{type: 'SourcingFilterPresetList'}],
    }),
    sourcingGetPreset: build.query<GetPresetApiResponse, GetPresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets/${queryArg.id}`,
      }),
      // providesTags so update/duplicate invalidations actually clear this per-preset cache
      // entry — otherwise lazy `preferCacheValue` callers serve stale filters after an edit.
      providesTags: (_, __, queryArg) => [{type: 'SourcingFilterPreset', id: queryArg.id}],
    }),
    sourcingReorderPresets: build.mutation<ReorderPresetsApiResponse, ReorderPresetsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets/reorder`,
        method: 'PUT',
        body: queryArg.reorderPresetsRequestBody,
      }),
      onQueryStarted: ({reorderPresetsRequestBody: body}, {dispatch, getState, queryFulfilled}) => {
        // OPTIMISTIC RESPONSE
        const queriesOriginalArgs = sourcingRtkApi.util
          .selectInvalidatedBy(getState(), [{type: 'SourcingFilterPresetList'}])
          .map((item) => item.originalArgs);
        queriesOriginalArgs.forEach((originalArgs) => {
          const patchResult = dispatch(
            sourcingRtkApi.util.updateQueryData('sourcingGetPresetsList', originalArgs, (draft) => {
              draft.forEach((item) => {
                const position = body.items.find((reqItem) => reqItem.id === item.id)?.position;
                if (isNotNil(position)) {
                  item.position = position;
                }
              });
            })
          );
          queryFulfilled.catch(patchResult.undo);
        });
      },
      invalidatesTags: () => [{type: 'SourcingFilterPresetList'}],
    }),
    sourcingGetCarFeatures: build.query<GetCarFeaturesApiResponse, GetCarFeaturesApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/car-features`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    sourcingGetTopSelling: build.query<
      SourcingGetTopSellingApiResponse,
      SourcingGetTopSellingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/sold-vehicle/top-selling`,
        method: 'POST',
        body: queryArg.body,
        params: {size: queryArg.size},
      }),
    }),
    sourcingGetPriceReport: build.mutation<
      SourcingGetPriceReportApiResponse,
      SourcingGetPriceReportApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/price-report`,
        method: 'POST',
        body: queryArg,
      }),
    }),
    sourcingVehicleDetail: build.query<
      SourcingVehicleDetailApiResponse,
      SourcingVehicleDetailApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/${encodeURIComponent(queryArg.externalId)}`,
        params: {
          marginLte: queryArg.marginLte,
          marginGte: queryArg.marginGte,
          positionGte: queryArg.positionGte,
          positionLte: queryArg.positionLte,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          pricingStrategy: queryArg.pricingStrategy,
        },
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SourcingVehicleDetail', id: queryArg.externalId},
      ],
    }),
    createSourcingVehicleForBusinessCase: build.mutation<
      CreateSourcingVehicleForBusinessCaseApiResponse,
      CreateSourcingVehicleForBusinessCaseApiArg
    >({
      query: ({externalId, make, model}) => ({
        url: `/dms/v1/sourcing-vehicle/${encodeURIComponent(externalId)}/business-case-vehicle`,
        method: 'POST',
        body: {make: make ?? null, model: model ?? null},
      }),
    }),
    /**
     * Live Alpha availability check (batch-ready). Results are also upserted into
     * `sourcingAvailabilityByAdId` so listing and detail can share per-adId status.
     */
    sourcingAvailabilityCheck: build.query<
      SourcingAvailabilityCheckApiResponse,
      SourcingAvailabilityCheckApiArg
    >({
      // Side-effect poll only: each `{adIds}` batch is a distinct cache key and callers always
      // forceRefetch. Durable status lives in `sourcingAvailabilityByAdId` — do not retain
      // these short-lived batch entries (would proliferate across the 2 min poll window).
      keepUnusedDataFor: 0,
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/availability-check`,
        method: 'POST',
        body: {adIds: queryArg.adIds},
      }),
      async onQueryStarted(_queryArg, {dispatch, getState, queryFulfilled}) {
        try {
          const {data} = await queryFulfilled;
          data.results.forEach((result) => {
            // List + detail can poll the same adId with different batch args (no RTK dedupe).
            // Never let an older in-flight `active` overwrite a newer `just_sold`.
            const existingStatus = sourcingRtkApi.endpoints.sourcingAvailabilityByAdId.select({
              adId: result.adId,
            })(getState()).data?.status;
            if (!shouldPersistAvailabilityCheckResult(existingStatus, result.status)) {
              return;
            }

            dispatch(
              sourcingRtkApi.util.upsertQueryData(
                'sourcingAvailabilityByAdId',
                {adId: result.adId},
                result
              )
            );
            patchVehicleAvailabilityInCaches(result.adId, result.status, {dispatch, getState});
          });
        } catch {
          // Caller (poll hook) handles toast / stop; leave caches unchanged.
        }
      },
    }),
    /**
     * Per-adId availability status cache (not a network endpoint).
     * Hydrated via upsertQueryData from `sourcingAvailabilityCheck`.
     * `queryFn` returns a non-terminal placeholder so subscribe/initiate never rejects;
     * real terminal data always comes from upsert.
     */
    sourcingAvailabilityByAdId: build.query<
      SourcingAvailabilityByAdIdApiResponse,
      SourcingAvailabilityByAdIdApiArg
    >({
      // Keep entries while detail (or listing) is subscribed; avoid GC wiping upserts.
      keepUnusedDataFor: SOURCING_KEEP_UNUSED_DATA_SECONDS,
      queryFn: (queryArg) => ({
        data: {adId: queryArg.adId, status: 'unknown'},
      }),
    }),
    /**
     * Accumulating list query for infinite scroll. All pages for a given (filters, currency,
     * orderBy) tuple merge into a single cache entry via `merge` (append). Polling runs against
     * the separate `sourcingVehicleListPoll` cache and prepends new items via `updateQueryData`.
     */
    sourcingVehicleList: build.query<SourcingVehicleListApiResponse, SourcingVehicleListApiArg>({
      query: (queryArg) => {
        const offset = getOffset(queryArg.page);
        return {
          url: `/dms/v1/sourcing-vehicle`,
          method: 'POST',
          body: {...queryArg.body, offset, size: ROWS_PER_PAGE},
          params: {
            offset,
            size: ROWS_PER_PAGE,
            currency: queryArg.currency,
            pricingStrategy: queryArg.pricingStrategy,
            orderBy: queryArg.orderBy,
          },
        };
      },
      serializeQueryArgs: ({endpointName, queryArgs}) =>
        // Page is intentionally NOT in the cache key — all pages for the same filter set
        // accumulate into one cache entry via `merge` (append).
        `${endpointName}_${queryArgs.currency}_${queryArgs.pricingStrategy}_${queryArgs.orderBy}_${JSON.stringify(queryArgs.body)}`,
      transformResponse: (response: SourcingVehicleListApiResponse, _meta, queryArg) => {
        updateScrollAttemptedOffset(response, queryArg.page);
        updateListEndReached(response, response.data.length);
        return response;
      },
      merge: (currentCache, newData, meta) => {
        const newVehicles = filterOutDuplicates(currentCache.data, newData.data);
        currentCache.data.push(...newVehicles);
        updateScrollAttemptedOffset(currentCache, meta.arg.page);
        updateListEndReached(currentCache, newData.data.length);
      },
      forceRefetch: (params) => {
        // Combined boundary mirrors `nextPage` in SourcingTable. See README §Infinite scroll.
        const currentPage = params.currentArg?.page ?? 1;
        const previousPage = params.previousArg?.page;

        const requestedOffset = getOffset(currentPage);
        const endpointData = params.endpointState?.data;
        const cachedLength = isSourcingVehicleListApiResponse(endpointData)
          ? endpointData.data.length
          : 0;
        const scrollAttemptedOffset = isSourcingVehicleListApiResponse(endpointData)
          ? endpointData.scrollAttemptedOffset
          : undefined;
        const cachedPageOffset = getInfiniteScrollBoundaryOffset(
          cachedLength,
          scrollAttemptedOffset
        );
        const shouldRefetch =
          requestedOffset === cachedPageOffset ||
          (requestedOffset > cachedPageOffset && currentPage === (previousPage ?? 1) + 1);

        return shouldRefetch;
      },
      providesTags: () => [
        {type: 'SourcingAllListsWithSameResponseStructure'},
        {type: 'SourcingVehicleList'},
      ],
      // We need to keep cache 30mins because when you go to vehicle detail and return back we have to scroll to last visited vehicle
      keepUnusedDataFor: SOURCING_KEEP_UNUSED_DATA_SECONDS,
    }),
    /**
     * Narrow polling endpoint that fetches one page of latest items without merging. The
     * AllOffers hook walks pages 1..N until it finds overlap with the accumulating cache, then
     * prepends the new items via `updateQueryData('sourcingVehicleList', ...)`. Each page lives
     * in its own cache entry keyed by `page` and expires immediately when no subscriber is
     * listening (`keepUnusedDataFor: 0`).
     */
    sourcingVehicleListPoll: build.query<SourcingVehicleListApiResponse, SourcingVehicleListApiArg>(
      {
        query: (queryArg) => {
          const offset = getOffset(queryArg.page);
          return {
            url: `/dms/v1/sourcing-vehicle`,
            method: 'POST',
            body: {...queryArg.body, offset, size: ROWS_PER_PAGE},
            params: {
              offset,
              size: ROWS_PER_PAGE,
              currency: queryArg.currency,
              pricingStrategy: queryArg.pricingStrategy,
              orderBy: queryArg.orderBy,
            },
          };
        },
        keepUnusedDataFor: 0,
      }
    ),
    sourcingGetVehicleCount: build.query<
      GetSourcingVehicleCountApiResponse,
      GetSourcingVehicleCountApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/_count`,
        method: 'POST',
        body: queryArg.body,
        params: {currency: queryArg.currency, pricingStrategy: queryArg.pricingStrategy},
      }),
      providesTags: ['SourcingVehicleList'],
    }),
    sourcingVehicleLocations: build.query<
      SourcingVehicleLocationsApiResponse,
      SourcingVehicleLocationsApiArg
    >({
      query: ({body, currency, pricingStrategy}) => ({
        url: `/dms/v1/sourcing-vehicle/locations`,
        method: 'POST',
        body,
        params: {currency, pricingStrategy},
      }),
      providesTags: [{type: 'SourcingVehicleList', id: 'LOCATIONS'}],
    }),
    sourcingAllowedCountryAndCurrency: build.query<
      AllowedCountryAndCurrencyApiResponse,
      AllowedCountryAndCurrencyApiArg
    >({
      query: () => ({
        url: `/dms/v1/sourcing/allowed-country-and-currency`,
      }),
    }),
    getSourcingUserList: build.infiniteQuery<ListUserResponseBody, SourcingUserListQuery, number>({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) =>
          hasMoreUserListPages(lastPage) ? lastPageParam + ROWS_PER_PAGE : undefined,
      },
      query: ({queryArg: {list, ...params}, pageParam}) => ({
        url: `/dms/v1/sourcing-vehicle/${list}`,
        params: {...params, size: ROWS_PER_PAGE, offset: pageParam},
      }),
      providesTags: (_result, _error, {list}) => [
        'SourcingAllListsWithSameResponseStructure',
        USER_LIST_TAGS[list],
      ],
    }),
    // Buy all reads every page without extending the visible infinite list's cache.
    getSourcingUserToBuyPage: build.query<
      ListUserResponseBody,
      Omit<SourcingUserListQuery, 'list'> & {offset: number}
    >({
      query: (params) => ({
        url: '/dms/v1/sourcing-vehicle/user-to-buy',
        params: {...params, size: ROWS_PER_PAGE},
      }),
      keepUnusedDataFor: 0,
    }),
    sourcingBulkDeleteUserToBuy: build.mutation<
      BulkDeleteUserToBuyApiResponse,
      BulkDeleteUserToBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-to-buy/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'user-to-buy');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'inCart', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'inCart', false);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingToBuyList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingBulkCreateUserToBuy: build.mutation<
      BulkCreateUserToBuyApiResponse,
      BulkCreateUserToBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-to-buy/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'inCart', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'inCart', true);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingToBuyList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingBulkDeleteUserHidden: build.mutation<
      BulkDeleteUserHiddenApiResponse,
      BulkDeleteUserHiddenApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-hidden/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'user-hidden');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'hidden', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'hidden', false);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingHiddenList'},
        {type: 'SourcingVehicleList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingBulkCreateUserHidden: build.mutation<
      BulkCreateUserHiddenApiResponse,
      BulkCreateUserHiddenApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-hidden/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'sourcingVehicleList');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'hidden', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'hidden', true);
      },
      invalidatesTags: (_result, error) => [
        {type: 'User'},
        {type: 'SourcingHiddenList'},
        {type: 'SourcingPriceMapVehicles'},
        ...(isNil(error) ? [{type: 'SourcingVehicleList' as const, id: 'LOCATIONS'}] : []),
      ],
    }),
    sourcingBulkDeleteUserFavourite: build.mutation<
      BulkDeleteUserFavouriteApiResponse,
      BulkDeleteUserFavouriteApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-favourite/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'user-favourite');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'favourite', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'favourite', false);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingFavouriteList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingBulkCreateUserFavourite: build.mutation<
      BulkCreateUserFavouriteApiResponse,
      BulkCreateUserFavouriteApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-favourite/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'favourite', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'favourite', true);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingFavouriteList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingReorderUserComparison: build.mutation<void, SortUserComparisonRequestApiArg>({
      query: (queryArg) => {
        const requestBody: SortUserComparisonRequestBody = queryArg.map((vehicle, index) => ({
          externalId: vehicle.sourcingVehicle.adId,
          position: index + 1,
        }));

        return {
          url: '/dms/v1/sourcing-vehicle/user-comparison/sort',
          method: 'POST',
          body: {
            sourcingVehiclesWithPositions: requestBody,
          },
        };
      },
      onQueryStarted(patch, mutationLifecycleApi) {
        sourcingRtkApi.util
          .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingComparisonList'}])
          .forEach(({endpointName, originalArgs}) => {
            if (endpointName === 'getSourcingUserList') {
              if (originalArgs.list !== 'user-comparison') {
                return;
              }
              const patchResult = mutationLifecycleApi.dispatch(
                sourcingRtkApi.util.updateQueryData(
                  'getSourcingUserList',
                  originalArgs,
                  (draft) => {
                    const loaded = draft.pages.flatMap((page) => page.data);
                    const byId = new Map(loaded.map((row) => [row.sourcingVehicle.adId, row]));
                    if (
                      loaded.length !== patch.length ||
                      byId.size !== patch.length ||
                      patch.some((row) => !byId.has(row.sourcingVehicle.adId))
                    ) {
                      return;
                    }
                    // Reorder this entry's own rows; another currency may have different prices.
                    let index = 0;
                    draft.pages.forEach((page) => {
                      const length = page.data.length;
                      page.data = patch
                        .slice(index, index + length)
                        .map((row) => byId.get(row.sourcingVehicle.adId))
                        .filter(isNotNil);
                      index += length;
                    });
                  }
                )
              );
              mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
              return;
            }
          });
      },
      invalidatesTags: [{type: 'SourcingComparisonList'}],
    }),
    sourcingBulkDeleteUserComparison: build.mutation<
      BulkDeleteUserComparisonApiResponse,
      BulkDeleteUserComparisonApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-comparison/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'user-comparison');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'comparison', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'comparison', false);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingComparisonList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingClearUserComparison: build.mutation<void, void>({
      query: () => ({
        url: '/dms/v1/sourcing-vehicle/user-comparison',
        method: 'DELETE',
      }),
      onQueryStarted(_args, mutationLifecycleApi) {
        sourcingRtkApi.util
          .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingComparisonList'}])
          .forEach(({endpointName, originalArgs}) => {
            if (endpointName !== 'getSourcingUserList' || originalArgs.list !== 'user-comparison') {
              return;
            }
            const patch = mutationLifecycleApi.dispatch(
              sourcingRtkApi.util.updateQueryData('getSourcingUserList', originalArgs, (draft) => {
                draft.pages.splice(1);
                draft.pageParams.splice(1);
                draft.pages.forEach((page) => {
                  page.data = [];
                  page.links.totalCount = 0;
                  page.links.hasMore = false;
                });
              })
            );
            mutationLifecycleApi.queryFulfilled.catch(patch.undo);
          });
        optimisticClearComparisonFlags(mutationLifecycleApi);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingVehicleList'},
        {type: 'SourcingVehicleDetail'},
        {type: 'SourcingComparisonList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingBulkCreateUserComparison: build.mutation<
      BulkCreateUserComparisonApiResponse,
      BulkCreateUserComparisonApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-comparison/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'comparison', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'comparison', true);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingComparisonList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingCreateUserVehicleSummary: build.mutation<void, CreateUserVehicleSummaryApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/vehicle-summary/${queryArg.sourcingVehicleId}`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(queryArgs, mutationLifecycleApi) {
        updateClassifiedsVehicleSummaryInCaches(
          queryArgs.sourcingVehicleId,
          queryArgs.body,
          mutationLifecycleApi
        );
        updateAuctionVehicleSummaryInCaches(
          queryArgs.sourcingVehicleId,
          queryArgs.body,
          mutationLifecycleApi
        );
      },
    }),
    sourcingGetSalesClaim: build.query<SalesClaim, {adId: string}>({
      query: ({adId}) => ({
        url: `/dms/v1/sourcing-vehicle/${adId}/sales-claim`,
      }),
    }),
    sourcingSetSalesClaim: build.mutation<SalesClaim, {adId: string; note: string | null}>({
      query: ({adId, note}) => ({
        url: `/dms/v1/sourcing-vehicle/${adId}/sales-claim`,
        method: 'PUT',
        body: {note},
      }),
      async onQueryStarted({adId}, {dispatch, queryFulfilled}) {
        try {
          const {data} = await queryFulfilled;
          dispatch(sourcingRtkApi.util.upsertQueryData('sourcingGetSalesClaim', {adId}, data));
        } catch {}
      },
    }),
    sourcingSetFeatureAsHighlighted: build.mutation<void, {externalId: string; featureKey: string}>(
      {
        query: ({externalId, featureKey}) => ({
          url: `/dms/v1/sourcing-vehicle/${externalId}/feature/${featureKey}`,
          method: 'PUT',
        }),
        onQueryStarted: (queryArgs, mutationLifecycleApi) => {
          updateFeatureHighlightInCaches(
            queryArgs.externalId,
            queryArgs.featureKey,
            true,
            mutationLifecycleApi
          );
        },
      }
    ),
    sourcingSetFeatureAsNotHighlighted: build.mutation<
      void,
      {externalId: string; featureKey: string}
    >({
      query: ({externalId, featureKey}) => ({
        url: `/dms/v1/sourcing-vehicle/${externalId}/feature/${featureKey}`,
        method: 'DELETE',
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        updateFeatureHighlightInCaches(
          queryArgs.externalId,
          queryArgs.featureKey,
          false,
          mutationLifecycleApi
        );
      },
    }),
    sourcingAddVehicleHighlightBulk: build.mutation<
      {highlights: HighlightResponseBody[]},
      {externalId: string; highlights: string[]}
    >({
      query: ({externalId, highlights}) => ({
        url: `/dms/v1/sourcing-vehicle/${externalId}/highlight/_bulk`,
        method: 'PUT',
        body: {highlights: highlights.map((highlight) => ({text: highlight}))},
      }),
      onQueryStarted: async (queryArgs, mutationLifecycleApi) => {
        const {data} = await mutationLifecycleApi.queryFulfilled;
        updateHighlightsInCaches(queryArgs.externalId, data.highlights, mutationLifecycleApi);
      },
    }),
    sourcingRemoveVehicleHighlight: build.mutation<void, {externalId: string; highlightId: string}>(
      {
        query: ({externalId, highlightId}) => ({
          url: `/dms/v1/sourcing-vehicle/${externalId}/highlight/${highlightId}`,
          method: 'DELETE',
        }),
        onQueryStarted: (queryArgs, mutationLifecycleApi) => {
          removeHighlightFromCaches(
            queryArgs.externalId,
            queryArgs.highlightId,
            mutationLifecycleApi
          );
        },
      }
    ),
    sourcingRemoveVehicleHighlightBulk: build.mutation<
      RemoveHighlightBulkApiResponse,
      RemoveHighlightBulkApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/${queryArg.externalId}/highlight/bulk-delete`,
        method: 'PUT',
        body: queryArg.removeHighlightBulkRequestBody,
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        removeHighlightsBulkFromCaches(
          queryArgs.externalId,
          queryArgs.removeHighlightBulkRequestBody.highlightIds,
          mutationLifecycleApi
        );
      },
    }),
    sourcingAddVehicleShortcomingBulk: build.mutation<
      {shortcomings: HighlightResponseBody[]},
      {externalId: string; shortcomings: string[]}
    >({
      query: ({externalId, shortcomings}) => ({
        url: `/dms/v1/sourcing-vehicle/${externalId}/shortcoming/_bulk`,
        method: 'PUT',
        body: {shortcomings: shortcomings.map((shortcoming) => ({text: shortcoming}))},
      }),
      onQueryStarted: async (queryArgs, mutationLifecycleApi) => {
        const {data} = await mutationLifecycleApi.queryFulfilled;
        updateShortcomingsInCaches(queryArgs.externalId, data.shortcomings, mutationLifecycleApi);
      },
    }),
    sourcingRemoveVehicleShortcoming: build.mutation<
      void,
      {externalId: string; shortcomingId: string}
    >({
      query: ({externalId, shortcomingId}) => ({
        url: `/dms/v1/sourcing-vehicle/${externalId}/shortcoming/${shortcomingId}`,
        method: 'DELETE',
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        removeShortcomingFromCaches(
          queryArgs.externalId,
          queryArgs.shortcomingId,
          mutationLifecycleApi
        );
      },
    }),
    sourcingRemoveVehicleShortcomingBulk: build.mutation<
      RemoveShortcomingBulkApiResponse,
      RemoveShortcomingBulkApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/${queryArg.externalId}/shortcoming/bulk-delete`,
        method: 'PUT',
        body: queryArg.removeShortcomingBulkRequestBody,
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        removeShortcomingsBulkFromCaches(
          queryArgs.externalId,
          queryArgs.removeShortcomingBulkRequestBody.shortcomingIds,
          mutationLifecycleApi
        );
      },
    }),
    sourcingDrillDown: build.query<DrillDownResponse, DrillDownApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sourcing/drill-down`,
        method: 'POST',
        body: queryArg,
      }),
      // TODO T20-30972 - fix response on BE
      transformResponse: (response: RawDrillDownResponse, meta, arg: DrillDownApiArg) =>
        fixAlfaBugsInDrillDownResponse(
          arg.levels.map((level) => level.level),
          response
        ),
    }),
    sourcingGetSourcingServers: build.query<SourcingServer[], {countryCode: string}>({
      query: ({countryCode}) => ({
        url: '/common/v1/sourcing-servers',
        query: {
          countryCode,
        },
      }),
    }),
    sourcingPushVehiclesToSale: build.mutation<void, PushVehiclesToSaleApiArg>({
      query: ({externalIds}) => ({
        url: '/dms/v1/sourcing-vehicle/user-to-buy/buy-vehicles',
        method: 'POST',
        body: {sourcingVehiclesToBuy: externalIds},
      }),
      onQueryStarted: ({externalIds}, {dispatch, queryFulfilled}) => {
        queryFulfilled
          .then(() => {
            dispatch(
              sourcingRtkApi.endpoints.sourcingBulkDeleteUserToBuy.initiate({body: {externalIds}})
            );
          })
          .catch(() => {
            // We handle failure on component level – here we just skip initiating another mutation
          });
      },
    }),
    sourcingExportVehiclesToCsv: build.mutation<UserListCsvExport, ExportVehiclesToCsvApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-to-buy/export/csv`,
        method: 'POST',
        body: {externalIds: queryArg.externalIds},
        responseHandler: readUserListCsvExportResponse,
        cache: 'no-cache',
        params: {
          sourcingType: queryArg.sourcingType,
          list: queryArg.list,
          buyerCountry: queryArg.buyerCountry,
          currency: queryArg.currency,
          pricingStrategy: queryArg.pricingStrategy,
          margin: queryArg.margin,
          orderBy: queryArg.orderBy,
        },
      }),
    }),
    sourcingGetComments: build.query<SourcingGetCommentsApiResponse, SourcingGetCommentsApiArg>({
      query: ({externalId}) => ({
        url: `/dms/v1/sourcing/comment/${externalId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SourcingComments', id: queryArg.externalId},
      ],
    }),
    sourcingCreateComment: build.mutation<
      SourcingCreateCommentApiResponse,
      SourcingCreateCommentApiArg
    >({
      query: ({externalId, body}) => ({
        url: `/dms/v1/sourcing/comment/${externalId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'SourcingComments', id: queryArg.externalId},
      ],
    }),
    sourcingDeleteComment: build.mutation<void, SourcingDeleteCommentApiArg>({
      query: ({externalId, uuid}) => ({
        url: `/dms/v1/sourcing/comment/${externalId}/${uuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'SourcingComments', id: queryArg.externalId},
      ],
    }),
    sourcingUpdateComment: build.mutation<
      SourcingUpdateCommentApiResponse,
      SourcingUpdateCommentApiArg
    >({
      query: ({externalId, uuid, body}) => ({
        url: `/dms/v1/sourcing/comment/${externalId}/${uuid}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'SourcingComments', id: queryArg.externalId},
      ],
    }),
    sourcingGetUserCostVehicleNote: build.query<
      DetailUserCostVehicleNoteApiResponse,
      DetailUserCostVehicleNoteApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}/note`,
      }),
    }),
    sourcingSetUserVehicleCostNote: build.mutation<void, SetUserVehicleCostNoteApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}/note`,
        method: 'PUT',
        body: queryArg.updateCostVehicleNoteRequestBody,
      }),
    }),
    sourcingGetDefaultUserPresetCost: build.query<DetailDefaultUserPresetCostApiResponse, void>({
      query: () => ({
        url: `/dms/v1/sourcing/cost/preset/_default`,
      }),
    }),
    sourcingCreateUserVehicleCost: build.mutation<void, CreateUserVehicleCostApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}/item`,
        method: 'POST',
        body: queryArg.userVehicleCostRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {
          type: 'SourcingVehicleCosts',
          id: queryArg.sourcingVehicleId,
        },
      ],
    }),
    sourcingCreateUserVehicleCosts: build.mutation<void, CreateUserVehicleCostsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}`,
        method: 'POST',
        body: queryArg.userVehicleCostsRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {
          type: 'SourcingVehicleCosts',
          id: queryArg.sourcingVehicleId,
        },
      ],
    }),
    sourcingGetUserCostVehicleList: build.query<
      UserCostVehicleListApiResponse,
      UserCostVehicleListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SourcingVehicleCosts', id: queryArg.sourcingVehicleId},
      ],
    }),
    sourcingDeleteUserVehicleCost: build.mutation<void, DeleteUserVehicleCostApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}/item/${queryArg.itemUuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {
          type: 'SourcingVehicleCosts',
          id: queryArg.sourcingVehicleId,
        },
      ],
    }),
    sourcingUpdateUserVehicleCost: build.mutation<void, UpdateUserVehicleCostApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}/item/${queryArg.itemUuid}`,
        method: 'PUT',
        body: queryArg.userVehicleCostRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {
          type: 'SourcingVehicleCosts',
          id: queryArg.sourcingVehicleId,
        },
      ],
    }),
    sourcingGetAdServerRedirect: build.query<AdServerRedirectResponse, AdServerRedirectApiArg>({
      query: (queryArg) => ({
        url: '/dms/v1/sourcing/change-url/sales-server-url',
        params: queryArg,
      }),
    }),
    // A read without side effects; it uses POST only to carry the filter body.
    sourcingGetDrillDownAdServerRedirect: build.query<
      DrillDownAdServerRedirectApiResponse,
      DrillDownAdServerRedirectApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/sourcing/change-url/drill-down-server-url',
        method: 'POST',
        body: queryArg.drillDownAdServerRedirectRequestBody,
      }),
    }),
    createRelativeFilter: build.mutation<
      CreateRelativeFilterApiResponse,
      CreateRelativeFilterApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/sourcing/relative-filter',
        method: 'POST',
        body: queryArg.createRelativeFilterRequestBody,
      }),
      invalidatesTags: [{type: 'RelativeFilter', id: 'LIST'}],
    }),
    getRelativeFilters: build.query<GetRelativeFiltersApiResponse, GetRelativeFiltersApiArg>({
      query: () => ({
        url: '/dms/v1/sourcing/relative-filter',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({id}) => ({type: 'RelativeFilter' as const, id})),
              {type: 'RelativeFilter' as const, id: 'LIST'},
            ]
          : [{type: 'RelativeFilter' as const, id: 'LIST'}],
    }),
    deleteRelativeFilter: build.mutation<
      DeleteRelativeFilterApiResponse,
      DeleteRelativeFilterApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/relative-filter/${queryArg.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'RelativeFilter', id: queryArg.id},
        {type: 'RelativeFilter', id: 'LIST'},
      ],
    }),
    updateRelativeFilter: build.mutation<
      UpdateRelativeFilterApiResponse,
      UpdateRelativeFilterApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/relative-filter/${queryArg.id}`,
        method: 'PATCH',
        body: queryArg.updateRelativeFilterRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'RelativeFilter', id: queryArg.id},
        {type: 'RelativeFilter', id: 'LIST'},
      ],
    }),
    reorderRelativeFilters: build.mutation<
      ReorderRelativeFiltersApiResponse,
      ReorderRelativeFiltersApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/sourcing/relative-filter/reorder',
        method: 'PUT',
        body: queryArg.reorderRelativeFiltersRequestBody,
      }),
      onQueryStarted: ({reorderRelativeFiltersRequestBody: body}, {dispatch, queryFulfilled}) => {
        const patchResult = dispatch(
          sourcingRtkApi.util.updateQueryData('getRelativeFilters', undefined, (draft) => {
            body.items.forEach(({id, position}) => {
              const relativeFilter = draft.find((item) => item.id === id);
              if (isNotNil(relativeFilter)) {
                relativeFilter.position = position;
              }
            });
            draft.sort((left, right) => left.position - right.position);
          })
        );

        queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: [{type: 'RelativeFilter', id: 'LIST'}],
    }),
    sourcingSetUserSeen: build.mutation<CreateUserSeenApiResponse, CreateUserSeenApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-seen`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: () => [{type: 'SourcingFilterPresetList'}],
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'seen',
          true
        );
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'isNew',
          false
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'seen',
          true
        );
      },
    }),
    sourcingGetPriceMap: build.query<GetPriceMapApiResponse, GetPriceMapApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/${queryArg.externalId}/price-map`,
        method: 'POST',
        body: queryArg.priceMapRequestBody,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SourcingPriceMap', id: queryArg.externalId},
      ],
    }),
    createSourcingVehicleOffer: build.query<
      CreateSourcingVehicleOfferApiResponse,
      CreateSourcingVehicleOfferApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/vehicle-offer`,
        method: 'POST',
        body: queryArg.createSourcingVehicleOfferRequestBody,
      }),
    }),
    getSourcingVehicleOffer: build.query<
      GetSourcingVehicleOfferApiResponse,
      GetSourcingVehicleOfferApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/vehicle-offer/${queryArg.offerId}`,
      }),
    }),
    sourcingBulkDeleteUserCarvagoBuy: build.mutation<
      BulkDeleteUserCarvagoBuyApiResponse,
      BulkDeleteUserCarvagoBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-carvago-buy/_bulk`,
        method: 'DELETE',
        body: queryArg.bulkDeleteUserCarvagoBuyRequestBody,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(
          {body: {externalIds: args.bulkDeleteUserCarvagoBuyRequestBody.externalIds}},
          mutationLifecycleApi,
          'user-carvago-buy'
        );
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: args.bulkDeleteUserCarvagoBuyRequestBody.externalIds}},
          mutationLifecycleApi,
          'carvagoBuy',
          false
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: args.bulkDeleteUserCarvagoBuyRequestBody.externalIds}},
          mutationLifecycleApi,
          'carvagoBuy',
          false
        );
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingCarvagoBuyList'}],
    }),
    sourcingBulkCreateUserCarvagoBuy: build.mutation<
      BulkCreateUserCarvagoBuyApiResponse,
      BulkCreateUserCarvagoBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-carvago-buy/_bulk`,
        method: 'POST',
        body: queryArg.bulkCreateUserCarvagoBuyRequestBody,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: args.bulkCreateUserCarvagoBuyRequestBody.externalIds}},
          mutationLifecycleApi,
          'carvagoBuy',
          true
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: args.bulkCreateUserCarvagoBuyRequestBody.externalIds}},
          mutationLifecycleApi,
          'carvagoBuy',
          true
        );
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingCarvagoBuyList'}],
    }),
    sourcingCreateUserCarvagoBuy: build.mutation<
      CreateUserCarvagoBuyApiResponse,
      CreateUserCarvagoBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-carvago-buy`,
        method: 'POST',
        body: queryArg.createUserCarvagoBuyRequestBody,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        const externalId = args.createUserCarvagoBuyRequestBody.externalId;
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [externalId]}},
          mutationLifecycleApi,
          'carvagoBuy',
          true
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: [externalId]}},
          mutationLifecycleApi,
          'carvagoBuy',
          true
        );
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingCarvagoBuyList'}],
    }),
    sourcingDeleteUserCarvagoBuy: build.mutation<
      DeleteUserCarvagoBuyApiResponse,
      DeleteUserCarvagoBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-carvago-buy/${queryArg.externalId}`,
        method: 'DELETE',
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(
          {body: {externalIds: [args.externalId]}},
          mutationLifecycleApi,
          'user-carvago-buy'
        );
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [args.externalId]}},
          mutationLifecycleApi,
          'carvagoBuy',
          false
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: [args.externalId]}},
          mutationLifecycleApi,
          'carvagoBuy',
          false
        );
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingCarvagoBuyList'}],
    }),
  }),
  overrideExisting: false,
});

export {sourcingRtkApi as sourcingApi};

export const {
  useLazyExportAllUserToBuyToCsvQuery,
  useSourcingCreatePresetMutation,
  useSourcingGetPresetsListQuery,
  useSourcingUpdatePresetMutation,
  useSourcingDeletePresetMutation,
  useSourcingMarkPresetAllSeenMutation,
  useSourcingGetCarFeaturesQuery,
  useLazySourcingGetCarFeaturesQuery,
  useLazySourcingGetPresetQuery,
  useSourcingReorderPresetsMutation,
  useSourcingGetTopSellingQuery,
  useSourcingGetPriceReportMutation,
  useSourcingVehicleDetailQuery,
  useCreateSourcingVehicleForBusinessCaseMutation,
  useLazySourcingAvailabilityCheckQuery,
  useSourcingVehicleListQuery,
  useGetSourcingUserListInfiniteQuery,
  useSourcingGetVehicleCountQuery,
  useSourcingVehicleLocationsQuery,
  useSourcingAllowedCountryAndCurrencyQuery,
  useSourcingBulkCreateUserFavouriteMutation,
  useSourcingBulkDeleteUserFavouriteMutation,
  useSourcingReorderUserComparisonMutation,
  useSourcingBulkCreateUserComparisonMutation,
  useSourcingBulkDeleteUserComparisonMutation,
  useSourcingClearUserComparisonMutation,
  useSourcingBulkCreateUserHiddenMutation,
  useSourcingBulkDeleteUserHiddenMutation,
  useSourcingBulkCreateUserToBuyMutation,
  useSourcingBulkDeleteUserToBuyMutation,
  useSourcingCreateUserVehicleSummaryMutation,
  useSourcingGetSalesClaimQuery,
  useSourcingSetSalesClaimMutation,
  useSourcingSetFeatureAsHighlightedMutation,
  useSourcingSetFeatureAsNotHighlightedMutation,
  useSourcingAddVehicleHighlightBulkMutation,
  useSourcingRemoveVehicleHighlightMutation,
  useSourcingRemoveVehicleHighlightBulkMutation,
  useSourcingAddVehicleShortcomingBulkMutation,
  useSourcingRemoveVehicleShortcomingMutation,
  useSourcingRemoveVehicleShortcomingBulkMutation,
  useSourcingDrillDownQuery,
  useSourcingGetSourcingServersQuery,
  useSourcingPushVehiclesToSaleMutation,
  useSourcingExportVehiclesToCsvMutation,
  useSourcingGetCommentsQuery,
  useSourcingCreateCommentMutation,
  useSourcingDeleteCommentMutation,
  useSourcingUpdateCommentMutation,
  useSourcingGetUserCostVehicleNoteQuery,
  useSourcingSetUserVehicleCostNoteMutation,
  useSourcingGetDefaultUserPresetCostQuery,
  useSourcingCreateUserVehicleCostMutation,
  useSourcingCreateUserVehicleCostsMutation,
  useSourcingGetUserCostVehicleListQuery,
  useSourcingDeleteUserVehicleCostMutation,
  useSourcingUpdateUserVehicleCostMutation,
  useLazySourcingGetAdServerRedirectQuery,
  useLazySourcingGetDrillDownAdServerRedirectQuery,
  useCreateRelativeFilterMutation,
  useGetRelativeFiltersQuery,
  useDeleteRelativeFilterMutation,
  useUpdateRelativeFilterMutation,
  useReorderRelativeFiltersMutation,
  useSourcingSetUserSeenMutation,
  useSourcingGetPriceMapQuery,
  useCreateSourcingVehicleOfferQuery,
  useLazyGetSourcingVehicleOfferQuery,
  useSourcingBulkDeleteUserCarvagoBuyMutation,
  useSourcingBulkCreateUserCarvagoBuyMutation,
  useSourcingCreateUserCarvagoBuyMutation,
  useSourcingDeleteUserCarvagoBuyMutation,
} = sourcingRtkApi;

export type SourcingCacheApi = Pick<OmneticMutationLifecycleApi<unknown>, 'dispatch' | 'getState'>;

/**
 * Writes live-check `justSold` onto cached list/detail/price-map vehicles.
 * Does not rewrite occurrence flags (`isAvailable` / `isSold`).
 * Safe to re-run when detail loads after a list check already terminalized ByAdId.
 */
export function patchVehicleAvailabilityInCaches(
  adId: string,
  status: AvailabilityCheckStatus,
  cacheApi: SourcingCacheApi
) {
  // Only confirmed live results rewrite `justSold`. `unsupported_platform` is terminal for
  // polling but leaves availability unknown (no justSold patch).
  if (status !== 'active' && status !== 'just_sold') {
    return;
  }

  const justSold = status === 'just_sold';

  patchSourcingListCaches(cacheApi, (draft) => {
    draft.data.forEach((vehicle) => {
      if (vehicle.sourcingVehicle.adId === adId) {
        vehicle.sourcingVehicle.justSold = justSold;
      }
    });
  });

  patchSourcingVehicleDetailCaches(cacheApi, adId, (draft) => {
    draft.sourcingVehicle.justSold = justSold;
  });

  patchSourcingPriceMapCaches(cacheApi, (draft) => {
    draft.cars.forEach((car) => {
      if (car.ad_id === adId) {
        car.just_sold = justSold;
      }
    });
  });
}

export function updateClassifiedsVehicleSummaryInCaches(
  sourcingVehicleId: string,
  body: CreateUserVehicleSummaryRequestBody,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  patchSourcingListCaches(mutationLifecycleApi, (draft) => {
    setUserVehicleSummary(sourcingVehicleId, body, draft);
  });

  getSourcingVehicleDetailQueriesOriginalArgs(sourcingVehicleId, mutationLifecycleApi).forEach(
    (originalArgs) => {
      const patchResult = mutationLifecycleApi.dispatch(
        sourcingRtkApi.util.updateQueryData('sourcingVehicleDetail', originalArgs, (draft) => ({
          ...draft,
          vehicleSummary: {
            ...draft.vehicleSummary,
            purchasePrice: body.purchasePrice ?? null,
            sellingPrice: body.sellingPrice ?? null,
            comment: body.comment ?? null,
            ...body,
          },
        }))
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    }
  );
}

/** Permanent (non-undo) patch of every cached classifieds list query. */
function patchSourcingListCaches(
  cacheApi: SourcingCacheApi,
  updateFn: (draft: SourcingVehicleListApiResponse | ListUserResponseBody) => void
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(cacheApi.getState(), [{type: 'SourcingAllListsWithSameResponseStructure'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName === 'getSourcingUserList') {
        cacheApi.dispatch(
          sourcingRtkApi.util.updateQueryData('getSourcingUserList', originalArgs, (draft) => {
            draft.pages.forEach(updateFn);
          })
        );
        return;
      }
      if (!isSourcingListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      cacheApi.dispatch(sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, updateFn));
    });
}

/** Permanent (non-undo) patch of cached detail queries for `externalId`. */
function patchSourcingVehicleDetailCaches(
  cacheApi: SourcingCacheApi,
  externalId: string,
  updateFn: (draft: SourcingVehicleDetailApiResponse) => void
) {
  getSourcingVehicleDetailQueriesOriginalArgs(externalId, cacheApi).forEach((originalArgs) => {
    cacheApi.dispatch(
      sourcingRtkApi.util.updateQueryData('sourcingVehicleDetail', originalArgs, updateFn)
    );
  });
}

/** Permanent (non-undo) patch of every cached price-map query. */
function patchSourcingPriceMapCaches(
  cacheApi: SourcingCacheApi,
  updateFn: (draft: GetPriceMapApiResponse) => void
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(cacheApi.getState(), [{type: 'SourcingPriceMap'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'sourcingGetPriceMap') {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      cacheApi.dispatch(
        sourcingRtkApi.util.updateQueryData('sourcingGetPriceMap', originalArgs, updateFn)
      );
    });
}

function getSourcingVehicleDetailQueriesOriginalArgs(
  externalId: string,
  cacheApi: Pick<OmneticMutationLifecycleApi<unknown>, 'getState'>
) {
  return sourcingRtkApi.util
    .selectInvalidatedBy(cacheApi.getState(), [{type: 'SourcingVehicleDetail'}])
    .filter(({endpointName, originalArgs}) => {
      if (endpointName !== 'sourcingVehicleDetail') {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }
      return externalId === originalArgs.externalId;
    })
    .map((item) => item.originalArgs);
}

function updateSourcingVehicleDetailCache(
  externalId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>,
  updateFn: (draft: SourcingVehicleDetailApiResponse) => void
) {
  getSourcingVehicleDetailQueriesOriginalArgs(externalId, mutationLifecycleApi).forEach(
    (originalArgs) => {
      const patchResult = mutationLifecycleApi.dispatch(
        sourcingRtkApi.util.updateQueryData('sourcingVehicleDetail', originalArgs, updateFn)
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    }
  );
}

export function updateFeatureHighlightInCaches(
  externalId: string,
  featureKey: string,
  isHighlight: boolean,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    assignSafely(draft.features, featureKey, {
      ...getSafely(draft.features, featureKey),
      isHighlight,
    });
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    assignSafely(draft.features, featureKey, {
      ...getSafely(draft.features, featureKey),
      isHighlight,
    });
  });
}

export function updateHighlightsInCaches(
  externalId: string,
  highlights: HighlightResponseBody[],
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = highlights;
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = highlights;
  });
}

export function removeHighlightFromCaches(
  externalId: string,
  highlightId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = draft.highlights.filter((h) => h.id !== highlightId);
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = (draft.highlights ?? []).filter((h) => h.id !== highlightId);
  });
}

export function removeHighlightsBulkFromCaches(
  externalId: string,
  highlightIds: string[],
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = draft.highlights.filter((h) => !highlightIds.includes(h.id));
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = (draft.highlights ?? []).filter((h) => !highlightIds.includes(h.id));
  });
}

export function updateShortcomingsInCaches(
  externalId: string,
  shortcomings: ShortcomingResponseBody[],
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = shortcomings;
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = shortcomings;
  });
}

export function removeShortcomingFromCaches(
  externalId: string,
  shortcomingId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = draft.shortcomings.filter((s) => s.id !== shortcomingId);
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = (draft.shortcomings ?? []).filter((s) => s.id !== shortcomingId);
  });
}

export function removeShortcomingsBulkFromCaches(
  externalId: string,
  shortcomingIds: string[],
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = draft.shortcomings.filter((s) => !shortcomingIds.includes(s.id));
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = (draft.shortcomings ?? []).filter((s) => !shortcomingIds.includes(s.id));
  });
}

// Zeroes the unread counter of a single preset in every cached presets list
function optimisticResetPresetNewCount(
  presetId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<SourcingMarkPresetAllSeenApiArg>
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingFilterPresetList'}])
    .forEach(({originalArgs}) => {
      const patchResult = mutationLifecycleApi.dispatch(
        sourcingRtkApi.util.updateQueryData('sourcingGetPresetsList', originalArgs, (draft) => {
          draft.forEach((preset) => {
            if (preset.id === presetId) {
              preset.newCount = 0;
            }
          });
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

// Drops the "new" highlight from already loaded list pages that belong to the given preset.
// Lists of other presets keep their flags - the backend watermark is per preset as well.
function optimisticClearIsNewInPresetListCaches(
  presetId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<SourcingMarkPresetAllSeenApiArg>
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingVehicleList'}])
    .forEach(({endpointName, originalArgs}) => {
      if (
        endpointName !== 'sourcingVehicleList' ||
        originalArgs?.body?.filterPresetId !== presetId
      ) {
        return;
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.data.forEach((vehicle) => {
            vehicle.isNew = false;
          });
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

function optimisticClearComparisonFlags(mutationLifecycleApi: OmneticMutationLifecycleApi<void>) {
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName === 'getSourcingUserList') {
        const patch = mutationLifecycleApi.dispatch(
          sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            draft.pages.forEach((page) =>
              page.data.forEach((vehicle) => assignSafely(vehicle, 'comparison', false))
            );
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patch.undo);
      } else if (isSourcingListEndpointName(endpointName)) {
        const patch = mutationLifecycleApi.dispatch(
          sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            draft.data.forEach((vehicle) => assignSafely(vehicle, 'comparison', false));
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patch.undo);
      }
    });
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingVehicleDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'sourcingVehicleDetail') {
        return;
      }
      const patch = mutationLifecycleApi.dispatch(
        sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          assignSafely(draft, 'comparison', false);
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patch.undo);
    });
}

// Gets affected vehicles details (those queries which provide `SourcingVehicleDetail` tag) and set their list assignment
function optimisticUpdateVehicleDetailCache<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  list: List,
  isInList: boolean
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingVehicleDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'sourcingVehicleDetail') {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      if (queryArgs.body.externalIds.includes(originalArgs.externalId)) {
        const patchResult = mutationLifecycleApi.dispatch(
          sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            assignSafely(draft, list, isInList);
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
      }
    });
}

// Gets all cached queries sourcingVehicleList and set sourcing vehicles list assignment
function optimisticUpdateVehicleListCaches<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  list: List,
  isInList: boolean
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName === 'getSourcingUserList') {
        const patch = mutationLifecycleApi.dispatch(
          sourcingRtkApi.util.updateQueryData('getSourcingUserList', originalArgs, (draft) => {
            draft.pages.forEach((page) =>
              page.data.forEach((vehicle) => {
                if (queryArgs.body.externalIds.includes(vehicle.sourcingVehicle.adId)) {
                  assignSafely(vehicle, list, isInList);
                }
              })
            );
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patch.undo);
        return;
      }
      if (!isSourcingListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.data.forEach((vehicle) => {
            if (queryArgs.body.externalIds.includes(vehicle.sourcingVehicle.adId)) {
              assignSafely(vehicle, list, isInList);
            }
          });
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

function optimisticRemoveVehiclesFromList<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  listToRemove?: SourcingUserListQuery['list'] | 'sourcingVehicleList'
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName === 'getSourcingUserList') {
        if (isNotNil(listToRemove) && originalArgs.list !== listToRemove) {
          return;
        }
        const patch = mutationLifecycleApi.dispatch(
          sourcingRtkApi.util.updateQueryData('getSourcingUserList', originalArgs, (draft) => {
            const removed = new Set<string>();
            draft.pages.forEach((page) => {
              page.data = page.data.filter((vehicle) => {
                if (!queryArgs.body.externalIds.includes(vehicle.sourcingVehicle.adId)) {
                  return true;
                }
                removed.add(vehicle.sourcingVehicle.adId);
                return false;
              });
            });
            draft.pages.forEach((page) => {
              if (isNotNil(page.links.totalCount)) {
                page.links.totalCount = Math.max(0, page.links.totalCount - removed.size);
              }
              if (removed.size > 0) {
                // Keep paging stopped until invalidation refetches the list: the old offset can
                // skip rows after a removal. If that refetch fails, a retry/remount is needed.
                page.links.hasMore = false;
              }
            });
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patch.undo);
        return;
      }
      if (!isSourcingListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      if (isNil(listToRemove) || endpointName === listToRemove) {
        const patchResult = mutationLifecycleApi.dispatch(
          sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            draft.data = draft.data.filter(
              (vehicle) => !queryArgs.body.externalIds.includes(vehicle.sourcingVehicle.adId)
            );
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
      }
    });
}
